<script setup lang="ts">
import { ClientOnly, CommonButton } from "#components";
import { ref, computed } from "vue";
import { useTheme } from "~/composables/useTheme";
import { useRoute } from "vue-router";

const props = defineProps<{
  navbarOpacity: number;
}>();

const drawer = ref(false);
const openDropdown = ref<string | null>(null);
const { colors, themeName } = useTheme();
const isDark = computed(() => themeName.value === "dark");

const route = useRoute();
const globalSearchOpen = useState("global-search-open", () => false);
const globalSearchQuery = useState("global-search-query", () => "");
const { investorUrl } = useInvestorPlatform();

const investEquirizeBaseUrl = computed(() => investorUrl("/login"));

function openSearch() {
  globalSearchOpen.value = true;
}

function onSearchInput(event?: Event) {
  // If triggered by focus, only open if it was keyboard focus (:focus-visible)
  if (event && (event as any).type === 'focus') {
    if ((event.target as HTMLElement).matches(':focus-visible')) {
      globalSearchOpen.value = true;
    }
    return;
  }
  // Otherwise (e.g. @input), always open if there's interaction
  globalSearchOpen.value = true;
}

function clearSearch() {
  globalSearchQuery.value = "";
}

function onSearchKeydown(e: KeyboardEvent) {
  if (e.key === "Escape") globalSearchOpen.value = false;
}

// Active link checker
const isActive = (link: any) => {
  if (link.path) {
    return route.path === link.path;
  }
  if (link.children) {
    return link.children.some((child: any) => route.path === child.path);
  }
  return false;
};

const getDropdownId = (name: string) => `${name.toLowerCase().replace(/\s+/g, "-")}-menu`;

const isDropdownOpen = (name: string) => openDropdown.value === name;

const openDropdownMenu = (name: string) => {
  openDropdown.value = name;
};

const closeDropdownMenu = (name: string) => {
  if (openDropdown.value === name) {
    openDropdown.value = null;
  }
};

const toggleDropdownMenu = (name: string, event?: Event) => {
  event?.preventDefault();
  openDropdown.value = openDropdown.value === name ? null : name;
};

const handleDropdownTriggerClick = (name: string, event: MouseEvent) => {
  event.preventDefault();
  toggleDropdownMenu(name, event);
};

const handleDropdownFocusOut = (event: FocusEvent, name: string) => {
  const dropdown = event.currentTarget as HTMLElement;
  if (!dropdown.contains(event.relatedTarget as Node | null)) {
    closeDropdownMenu(name);
  }
};

const navLinks = [
  { name: "Home", path: "/" },
  {
    name: "Products",
    children: [
      { name: "Bonds", path: "/products/bonds" },
      { name: "Fixed Deposits", path: "/products/fixedDeposits" },
    ],
  },
  { name: "About Us", path: "/about" },
  {
    name: "Resources",
    children: [
      { name: "Blogs", path: "/resources/blogs" },
      { name: "News", path: "/resources/news" },
    ],
  },
  { name: "Contact Us", path: "/contact-us" },
  { name: "Refer & Earn", path: "/refer" },
];

if (typeof window !== "undefined") {
  window.addEventListener('close-all-overlays', () => {
    drawer.value = false;
  });
}

const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && drawer.value) {
    drawer.value = false;
  }
};


const skipNavigation = () => {
  const mainElement = document.querySelector('main');
  if (mainElement) {
    if (!mainElement.hasAttribute('tabindex')) {
      mainElement.setAttribute('tabindex', '-1');
    }
    mainElement.focus();
  } else {
    // Fallback for next focusable element outside top bar
    const focusableElements = Array.from(document.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
    let foundTopBar = false;
    for (const el of focusableElements) {
      if (el.closest('.top-bar')) {
        foundTopBar = true;
      } else if (foundTopBar) {
        el.focus();
        break;
      }
    }
  }
};
</script>

<template>
  <nav aria-label="Main" :style="{ opacity: props.navbarOpacity, transition: 'opacity 0.3s ease' }">
    <div class="custom-navbar" :style="{ backgroundColor: colors.navBg, color: colors.text }">
      <button class="skip-nav-link" @click="skipNavigation" @keydown.enter="skipNavigation"
        @keydown.space.prevent="skipNavigation">
        Skip to main content
      </button>
      <!-- Brand -->
      <NuxtLink to="/" aria-label="Equirize Home">
        <img v-if="isDark" src="/icons/Logo-dark.svg" class="brand img-fluid" alt="Equirize Logo" />
        <img v-else src="/icons/Logo-light.svg" class="brand img-fluid" alt="Equirize Logo" />

      </NuxtLink>

      <!-- Desktop / Tablet Links -->
      <ul class="nav-links">
        <li v-for="link in navLinks" :key="link.name" class="nav-item" :class="{ active: isActive(link) }">
          <!-- Dropdown -->
          <template v-if="link.children">
            <div class="dropdown" :class="{ 'dropdown--open': isDropdownOpen(link.name) }"
              @mouseenter="openDropdownMenu(link.name)" @mouseleave="closeDropdownMenu(link.name)"
              @focusin="openDropdownMenu(link.name)" @focusout="handleDropdownFocusOut($event, link.name)"
              @keydown.escape.stop="closeDropdownMenu(link.name)">
              <button type="button" class="dropdown-trigger" :style="{ color: colors.text }" aria-haspopup="true"
                :aria-expanded="isDropdownOpen(link.name)" :aria-controls="getDropdownId(link.name)"
                :aria-label="`${link.name} menu`" @click="handleDropdownTriggerClick(link.name, $event)"
                @keydown.enter="toggleDropdownMenu(link.name, $event)"
                @keydown.space="toggleDropdownMenu(link.name, $event)">
                {{ link.name }}
                <span class="dropdown-chevron" aria-hidden="true"></span>
              </button>
              <ul :id="getDropdownId(link.name)" class="dropdown-menu" :style="{ backgroundColor: colors.primary }">
                <li v-for="sublink in link.children" :key="sublink.name"
                  :class="{ active: route.path === sublink.path }">
                  <a :href="sublink.path" :style="{ color: colors.text }"
                    :aria-current="route.path === sublink.path ? 'page' : undefined"
                    @click="closeDropdownMenu(link.name)"
                    @keydown.space.prevent="($event.target as HTMLElement).click()">
                    {{ sublink.name }}
                  </a>
                </li>
              </ul>
            </div>
          </template>

          <!-- Regular link -->
          <template v-else>
            <NuxtLink :to="link.path" :style="{ color: colors.text }" class="nav-link"
              :aria-current="isActive(link) ? 'page' : undefined"
              @keydown.space.prevent="($event.target as HTMLElement).click()">
              {{ link.name }}
            </NuxtLink>
          </template>
        </li>
      </ul>

      <!-- Mobile: search + hamburger grouped on the right -->
      <div class="navbar-mobile-actions mobile-view">
        <button type="button" class="search-trigger" :style="{ color: colors.text }" aria-label="Search"
          @click="openSearch" @keydown.enter.prevent="openSearch" @keydown.space.prevent="openSearch">
          <span class="search-icon" aria-hidden="true"></span>
        </button>
        <div class="hamburger" @click="drawer = true" :style="{ backgroundColor: colors.buttonBg }" role="button"
          tabindex="0" aria-label="Open navigation menu" @keydown.enter="drawer = true"
          @keydown.space.prevent="drawer = true">
          <div class="bars">
            <span class="bar barTop"></span>
            <span class="bar barMiddle"></span>
            <span class="bar barBottom"></span>
          </div>
        </div>
      </div>

      <!-- Fullscreen Mobile Drawer -->
      <transition name="slide">
        <div v-if="drawer" class="mobile-drawer" :style="{ backgroundColor: colors.primary }" @keydown="handleKeydown">
          <button class="close-btn" @click="drawer = false" @keydown.enter.prevent="drawer = false"
            @keydown.space.prevent="drawer = false" aria-label="Close navigation menu" ref="closeBtn">
            ✕
          </button>

          <div class="mobile-menu">
            <div v-for="link in navLinks" :key="link.name" class="mobile-item" :class="{ active: isActive(link) }">
              <!-- Parent with children -->
              <template v-if="link.children">
                <div class="mobile-dropdown">
                  <span class="mobile-dropdown-title">{{ link.name }}</span>
                  <div class="mobile-submenu">
                    <div v-for="sublink in link.children" :key="sublink.name" class="mobile-subitem"
                      :class="{ active: route.path === sublink.path }">
                      <div class="flex items-center gap-2">
                        <span class="mobile-bullet" aria-hidden="true"></span>
                        <a :href="sublink.path" @click="drawer = false">
                          {{ sublink.name }}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Single link -->
              <template v-else>
                <a :href="link.path" @click="drawer = false">
                  {{ link.name }}
                </a>
              </template>
            </div>
          </div>

          <!-- Mobile Get Started button -->
          <div class="mobile-get-started">
            <CommonButton text="Get Started" :color="colors.buttonBg" width="150px" height="40px"
              :href="investEquirizeBaseUrl" target="_blank" ctaName="get-started-navbar-mobile"
              ariaLabel="Get Started with Equirize (opens in new tab)" class="sign_up_initiated" />
          </div>
        </div>
      </transition>

      <!-- Desktop: search bar + dropdown anchor (panel slides down from here, no overlay) -->
      <div class="navbar-search-wrap desktop-view">
        <div class="navbar-search-bar"
          :style="{ '--search-input-bg': colors.inputBg, '--search-border': colors.inputBorder, '--search-text': colors.text, '--search-placeholder': colors.inputPlaceholder }">
          <button tabindex="-1" type="button" class="navbar-search-btn" aria-label="Search">
            <span class="search-icon" aria-hidden="true"></span>
          </button>
          <input v-model="globalSearchQuery" type="search" class="navbar-search-input"
            placeholder="Search bonds or FDs..." autocomplete="off" @focus="onSearchInput" @input="onSearchInput"
            @keydown="onSearchKeydown" />
          <button v-if="globalSearchQuery.length" type="button" class="navbar-search-clear" aria-label="Clear"
            @click="clearSearch" @keydown.enter.prevent="clearSearch" @keydown.space.prevent="clearSearch">
            ×
          </button>
        </div>
        <div id="search-dropdown-anchor" class="search-dropdown-anchor" aria-hidden="true"></div>
      </div>
      <!-- Desktop Get Started button -->
      <CommonButton text="Get Started" :color="colors.buttonBg" width="150px" height="40px"
        :href="investEquirizeBaseUrl" target="_blank" ctaName="get-started-navbar-desktop"
        ariaLabel="Get Started with Equirize (opens in new tab)" class="desktop-view sign_up_initiated" />
    </div>
  </nav>
</template>

<style scoped>
/* Navbar wrapper */
.custom-navbar {
  display: flex;
  justify-content: space-between;
  width: auto;
  margin: 0 26px auto;
  padding: 12px 24px;
  border-radius: 50px;
  transition: background-color 0.3s ease;
  align-items: center;
}

/* Brand */
.brand {
  width: 110px;
}

/* Desktop / Tablet Links */
.nav-links {
  list-style: none;
  display: flex;
  gap: 2rem;
  margin: 0;
  padding: 0;
  align-items: center;
}

.nav-links a {
  color: inherit;
  text-decoration: none;
  font-weight: 400;
  transition: color 0.2s ease;
}

.nav-links a:visited,
.dropdown-menu li a:visited,
.mobile-item a:visited,
.mobile-subitem a:visited {
  color: inherit;
}

.nav-links a:hover {
  color: var(--secondary, #0027AF);
}

.nav-link {
  display: block;
  width :100%
}

.dropdown-trigger {
  color: inherit;
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-weight: 400;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  transition: color 0.2s ease;
}

.dropdown-trigger:hover {
  color: var(--secondary, #0027AF);
}

.dropdown-chevron {
  width: 8px;
  height: 8px;
  border-right: 2px solid currentColor;
  border-bottom: 2px solid currentColor;
  transform: rotate(45deg) translateY(-2px);
}

/* Dropdown */
.nav-item {
  position: relative;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 160px;
  display: none;
  list-style: none;
  padding: 0.5rem 0;
  border-radius: 8px;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transform: translateY(10px);
  transition: opacity 0.2s, transform 0.2s, visibility 0.2s;
}

.dropdown--open .dropdown-menu {
  display: block;
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.dropdown-menu li a {
  display: block;
  padding: 0.5rem 1rem;
}

/* Active Highlight */
.nav-item.active>a,
.nav-item.active>.dropdown>a,
.nav-item.active>.dropdown>.dropdown-trigger,
.nav-item .dropdown-menu li.active a,
.mobile-item.active>a,
.mobile-subitem.active a {
  color: var(--secondary, #0027AF) !important;
  font-weight: 600;
}

/* Wrapper so dropdown anchors below the search bar */
.navbar-search-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
}

.search-dropdown-anchor {
  position: absolute;
  top: 100%;
  right: 0;
  left: auto;
  width: 640px;
  max-width: min(640px, 95vw);
  min-width: 320px;
  z-index: 2000;
  pointer-events: none;
}

.search-dropdown-anchor>* {
  pointer-events: auto;
}

/* Desktop search bar in navbar: rounded, light border, no icon background */
.navbar-search-bar {
  display: flex;
  align-items: center;
  height: 44px;
  min-width: 260px;
  max-width: 420px;
  border-radius: 22px;
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--search-border) 50%, transparent);
  background: var(--search-input-bg);
}

.navbar-search-btn {
  flex-shrink: 0;
  width: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: var(--search-text);
  cursor: default;
}

.search-icon {
  position: relative;
  width: 11px;
  height: 11px;
  display: inline-block;
  border: 2px solid currentColor;
  border-radius: 50%;
}

.search-icon::after {
  content: "";
  position: absolute;
  width: 8px;
  height: 2px;
  right: -6px;
  bottom: -3px;
  border-radius: 2px;
  background: currentColor;
  transform: rotate(45deg);
}

.navbar-search-input {
  flex: 1;
  min-width: 0;
  padding: 0 12px;
  border: none;
  background: transparent;
  color: var(--search-text);
  font-size: 14px;
  outline: none;
}

.navbar-search-input::placeholder {
  color: var(--search-placeholder);
}

.navbar-search-input:focus-visible {
  outline: none;
}

.navbar-search-clear {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  margin: 0 5px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--search-text);
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.8;
}

.navbar-search-clear:focus-visible {
  border-radius: 50%;
  outline-offset: 1px !important;
}

.navbar-search-clear:hover {
  opacity: 1;
}

/* Search trigger (mobile) */
.search-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border: none;
  background: transparent;
  border-radius: 50%;
  cursor: pointer;
  transition: opacity 0.2s ease;
}

.search-trigger:hover {
  opacity: 0.8;
  background: rgba(128, 128, 128, 0.1);
}

/* Hamburger for mobile */
.hamburger {
  display: none;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  width: 46px;
  height: 46px;
  display: flex;
  border-radius: 30px;
}

.bars {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 55%;
}

.bars span {
  display: block;
  height: 2px;
  border-radius: 2px;
  transition: background-color 0.3s ease;
  background: white;
}

.barTop {
  width: 18px;
  align-self: flex-end;
}

.barMiddle {
  width: 25px;
  align-self: center;
}

.barBottom {
  width: 18px;
  align-self: flex-start;
}

/* Fullscreen mobile drawer */
.mobile-drawer {
  position: fixed;
  overflow-y: scroll;
  top: 0;
  right: 0;
  width: 100%;
  height: 100vh;
  z-index: 999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  transition: opacity 0.3s ease;
}

.mobile-menu {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.mobile-item a,
.mobile-dropdown-title,
.mobile-subitem {
  font-size: 1.2rem;
  text-decoration: none;
  cursor: pointer;
  color: inherit;
}

.mobile-submenu {
  margin-top: 0.5rem;
  margin-left: 10px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mobile-subitem a {
  font-size: 1.2rem;
  font-weight: normal;
  display: block;
  padding: 6px 0;
}

.mobile-bullet {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

/* Mobile Get Started */
.mobile-get-started {
  margin-top: 2rem;
  text-align: center;
}

/* Close button */
.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
  font-size: 2rem;
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
}

/* Slide animation */
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from {
  transform: translateX(100%);
}

.slide-enter-to {
  transform: translateX(0%);
}

.slide-leave-from {
  transform: translateX(0%);
}

.slide-leave-to {
  transform: translateX(100%);
}

/* Responsive breakpoints */
/* Mobile: group search + hamburger with small gap */
.navbar-mobile-actions {
  display: none;
  align-items: center;
  gap: 14px;
}

/* Skip nav link button classes */


.skip-nav-link {
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  position: absolute;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.skip-nav-link:focus,
.skip-nav-link:focus-visible {
  width: auto;
  height: auto;
  clip: auto;
  white-space: normal;
  position: relative;
  margin: 0;
  padding: 8px 16px;
  background: var(--buttonBg);
  color: var(--buttonText);
  border: 1px solid var(--color-primary-40, #3182ce);
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  z-index: 1000;
  outline: 2px solid var(--color-primary-40, #3182ce);
  outline-offset: 2px;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}


@media (max-width: 1024px) {
  .nav-links {
    display: none;
  }

  .navbar-search-wrap.desktop-view {
    display: none;
  }

  .search-trigger.desktop-view {
    display: none;
  }

  .navbar-mobile-actions.mobile-view {
    display: flex;
  }

  .navbar-mobile-actions .search-trigger {
    display: flex;
  }

  .hamburger {
    display: flex;
  }
}

@media (min-width: 1025px) {
  .search-trigger.mobile-view {
    display: none;
  }

  .search-trigger.desktop-view,
  .navbar-search-wrap.desktop-view {
    display: flex;
  }
}

@media (min-width: 1024px) {
  .custom-navbar {
    width: 90%;
    margin: 0 auto;
  }
}

@media (max-height: 500px) and (orientation: landscape) {

  .mobile-drawer {
    justify-content: start;
    padding: 20px 0;
  }

  .mobile-get-started {
    margin-top: 1rem;
  }
}

</style>
